type Runtime = 'client' | 'server' | 'edge';

const UNRESOLVED_ENV_PLACEHOLDERS = new Set([
  '$VERCEL_GIT_COMMIT_SHA',
  '${VERCEL_GIT_COMMIT_SHA}',
]);

function isUnresolvedEnvPlaceholder(value: string) {
  const trimmed = value.trim();
  if (UNRESOLVED_ENV_PLACEHOLDERS.has(trimmed)) return true;
  // Copied from docs/shell into Vercel env without linking the variable.
  if (/^\$[A-Z][A-Z0-9_]*$/.test(trimmed)) return true;
  return false;
}

export function getSentryEnvironment() {
  return process.env.VERCEL_ENV ?? process.env.NODE_ENV ?? 'development';
}

/** Ties errors and release-health sessions to a deploy (see Sentry Release Health). */
export function getSentryRelease() {
  const candidates = [
    process.env.SENTRY_RELEASE,
    process.env.VERCEL_GIT_COMMIT_SHA,
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  ];

  for (const candidate of candidates) {
    const release = candidate?.trim();
    if (!release || isUnresolvedEnvPlaceholder(release)) continue;
    return release;
  }

  return undefined;
}

/**
 * Sentry's build plugin reads `SENTRY_RELEASE` directly. Remove unresolved placeholders
 * so Vercel can fall back to `VERCEL_GIT_COMMIT_SHA` / git detection.
 */
export function sanitizeSentryBuildEnv() {
  const release = process.env.SENTRY_RELEASE?.trim();
  if (release && isUnresolvedEnvPlaceholder(release)) {
    delete process.env.SENTRY_RELEASE;
  }
}

export function getSentryDsn(runtime: Runtime) {
  if (runtime === 'client') {
    return process.env.NEXT_PUBLIC_SENTRY_DSN ?? process.env.SENTRY_DSN;
  }

  return process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN;
}

export function getTracesSampleRate() {
  return process.env.NODE_ENV === 'development' ? 1 : 0.1;
}

export function isSentryEnabled(runtime: Runtime) {
  return Boolean(getSentryDsn(runtime));
}
